import * as THREE from 'three'
import {
  beginGSplatResourceAttempt,
  measureGSplatLoadStage,
  measureGSplatLoadStageSync,
} from '@/plugins/gsplat/telemetry/GSplatLoadTelemetry'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatLoadTelemetryObserver } from '@/shared/types/gsplatTelemetry'

const log = getLoggerManager().getLogger('gsplat:loader')

function createAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw createAbortError()
  }
}

/**
 * SOGS Meta 结构（v1 原始格式）
 */
export interface SogsMetaV1 {
  /** 位置量化参数及纹理文件，shape 记录 splat 数量 */
  means: {
    shape: [number]
    mins: [number, number, number]
    maxs: [number, number, number]
    files: string[]
  }
  /** 缩放量化参数及纹理文件 */
  scales: {
    mins: [number, number, number]
    maxs: [number, number, number]
    files: string[]
  }
  /** 四元数纹理文件 */
  quats: {
    files: string[]
  }
  /** SH DC 量化参数及纹理文件 */
  sh0: {
    mins: [number, number, number, number]
    maxs: [number, number, number, number]
    files: string[]
  }
  /** SH 高阶系数信息，无高阶 SH 时省略 */
  shN?: {
    mins: number
    maxs: number
    files: string[]
  }
}

/**
 * SOGS Meta 结构（v2 升级格式）
 */
export interface SogsMetaV2 {
  /** 格式版本，1 为 min-max，2 为 codebook */
  version: 1 | 2
  /** splat 总数 */
  count: number
  /** 位置量化参数及纹理文件 */
  means: {
    mins: [number, number, number]
    maxs: [number, number, number]
    files: string[]
  }
  /** 缩放量化参数或 codebook */
  scales: {
    mins?: [number, number, number]
    maxs?: [number, number, number]
    codebook?: number[]
    files: string[]
  }
  /** 四元数纹理文件 */
  quats: {
    files: string[]
  }
  /** SH DC 量化参数或 codebook */
  sh0: {
    mins?: [number, number, number, number]
    maxs?: [number, number, number, number]
    codebook?: number[]
    files: string[]
  }
  /** SH 高阶系数信息，无高阶 SH 时省略 */
  shN?: {
    count?: number
    mins?: number
    maxs?: number
    bands?: number
    codebook?: number[]
    files: string[]
  }
}

/**
 * SOGS Meta 联合类型（支持v1和v2）
 */
export type SogsMeta = SogsMetaV1 | SogsMetaV2

/**
 * SOGS 纹理数据结构
 */
export interface SogsTextureData {
  /** 位置低位纹理 */
  means_l: THREE.Texture
  /** 位置高位纹理 */
  means_u: THREE.Texture
  /** 四元数纹理 */
  quats: THREE.Texture
  /** 缩放纹理 */
  scales: THREE.Texture
  /** SH DC 颜色纹理 */
  sh0: THREE.Texture
  /** SH palette 数据纹理，无高阶 SH 时省略 */
  sh_centroids?: THREE.Texture
  /** SH palette 索引纹理，无高阶 SH 时省略 */
  sh_labels?: THREE.Texture
}

/**
 * SOGS 加载选项。
 */
export interface SogsParserLoadOptions {
  mapUrl?: (filename: string) => string
  onProgress?: (loaded: number, total: number) => void
  /** 是否加载高阶球谐数据 SHN，默认 true；SH0 基础颜色始终加载 */
  loadSHN?: boolean
  /** 是否携带跨域 Cookie（用于 CloudFront 签名 Cookie 等场景） */
  withCredentials?: boolean
  /** 中止信号 */
  signal?: AbortSignal
  /** 单次模型加载的只读遥测 observer */
  telemetry?: GSplatLoadTelemetryObserver
  /** 同一资源从 1 开始递增的底层 attempt 序号 */
  telemetryAttempt?: number
}

/**
 * 类型守卫：判断是否为 V1 格式。
 *
 * @param meta - 待检测的元数据对象
 * @returns 是 V1 格式时返回 true
 */
export function isSogsMetaV1(meta: SogsMeta | Record<string, unknown>): meta is SogsMetaV1 {
  return (
    'means' in meta &&
    typeof meta.means === 'object' &&
    meta.means !== null &&
    'shape' in meta.means
  )
}

/**
 * 类型守卫：判断是否为 V2 格式。
 *
 * @param meta - 待检测的元数据对象
 * @returns 是 V2 格式时返回 true
 */
export function isSogsMetaV2(meta: SogsMeta | Record<string, unknown>): meta is SogsMetaV2 {
  return (
    'version' in meta &&
    typeof meta.version === 'number' &&
    (meta.version === 1 || meta.version === 2)
  )
}

/**
 * 为本次加载创建有效 metadata。关闭 SHN 时不修改解析得到的原始对象。
 */
export function createEffectiveSogsMeta(meta: SogsMetaV2, loadSHN = true): SogsMetaV2 {
  if (loadSHN) {
    return meta
  }

  const { shN: _shN, ...effectiveMeta } = meta
  return effectiveMeta
}

/**
 * SOGS Parser - 解析 SOGS 压缩格式
 *
 * 参考 PlayCanvas sogs.js
 * 使用无预乘、无色彩转换的 ImageBitmap 直接上传属性纹理。
 */
export class SogsParser {
  private static fetchInit(options?: SogsParserLoadOptions): RequestInit | undefined {
    if (!options?.withCredentials && !options?.signal) {
      return undefined
    }

    const init: RequestInit = {}
    if (options.withCredentials) {
      init.credentials = 'include'
    }
    if (options.signal) {
      init.signal = options.signal
    }
    return init
  }

  /**
   * 升级 v1 meta 到 v2 格式，参考 PlayCanvas sogs.js upgradeMeta()。
   *
   * @param meta - v1 格式的原始元数据
   * @returns 转换后的 v2 元数据
   */
  static upgradeMeta(meta: SogsMetaV1): SogsMetaV2 {
    const result: SogsMetaV2 = {
      version: 1,
      count: meta.means.shape[0],
      means: {
        mins: meta.means.mins,
        maxs: meta.means.maxs,
        files: meta.means.files,
      },
      scales: {
        mins: meta.scales.mins,
        maxs: meta.scales.maxs,
        files: meta.scales.files,
      },
      quats: {
        files: meta.quats.files,
      },
      sh0: {
        mins: meta.sh0.mins,
        maxs: meta.sh0.maxs,
        files: meta.sh0.files,
      },
    }

    if (meta.shN) {
      result.shN = {
        mins: meta.shN.mins,
        maxs: meta.shN.maxs,
        files: meta.shN.files,
      }
    }

    return result
  }

  /**
   * 加载单个 WebP 纹理为 GPU 纹理。
   *
   * 属性通道存储量化索引，不能经过 Canvas 2D 的 alpha 预乘/反预乘。
   * 保留 ImageBitmap 供首次上传及 context restore 使用，在纹理销毁时关闭。
   *
   * @param url - WebP 资源 URL
   * @returns 待上传到 GPU 的 Texture
   */
  static async loadTexture(
    url: string,
    options: SogsParserLoadOptions = {}
  ): Promise<THREE.Texture> {
    const { signal } = options
    throwIfAborted(signal)
    const attempt = beginGSplatResourceAttempt(options.telemetry, {
      resourceType: 'sog-texture',
      resourceIdentity: url,
      resourceUrl: url,
      attempt: options.telemetryAttempt,
    })
    let blob: Blob
    try {
      const response = await fetch(url, SogsParser.fetchInit(options))
      attempt.markResponse(response)
      if (!response.ok) {
        attempt.fail(new Error('SOG texture request failed'), `http-${response.status}`)
        throw new Error(`Failed to load SOG texture: ${response.status}`)
      }
      blob = await response.blob()
      throwIfAborted(signal)
      attempt.complete(blob.size)
    } catch (error) {
      attempt.fail(error)
      throw error
    }

    // 使用 createImageBitmap 保持原始数据（不做色彩空间转换）
    const imageBitmap = await measureGSplatLoadStage(
      options.telemetry,
      {
        resourceType: 'sog-texture',
        resourceIdentity: url,
        resourceUrl: url,
        attempt: options.telemetryAttempt,
        stage: 'texture-decode',
      },
      () =>
        createImageBitmap(blob, {
          colorSpaceConversion: 'none',
          premultiplyAlpha: 'none',
        })
    )

    let texture: THREE.Texture | undefined
    try {
      throwIfAborted(signal)

      texture = new THREE.Texture(imageBitmap)
      const ownedTexture = texture
      const disposeImage = () => {
        ownedTexture.removeEventListener('dispose', disposeImage)
        imageBitmap.close()
        ownedTexture.image = null
      }
      texture.addEventListener('dispose', disposeImage)
      texture.minFilter = THREE.NearestFilter
      texture.magFilter = THREE.NearestFilter
      texture.generateMipmaps = false
      texture.colorSpace = THREE.NoColorSpace // 关键：保持原始数据
      texture.flipY = false // WebP 不需要翻转
      texture.needsUpdate = true
      throwIfAborted(signal)

      return texture
    } catch (error) {
      if (texture) {
        texture.dispose()
      } else {
        imageBitmap.close()
      }
      if (signal?.aborted) {
        throw createAbortError()
      }
      throw error
    }
  }

  /**
   * 加载 SOGS 格式数据
   *
   * @param metaUrl meta.json 的 URL
   * @param options 加载选项
   * @returns 纹理数据和元数据
   */
  static async load(
    metaUrl: string,
    options: SogsParserLoadOptions = {}
  ): Promise<{ textures: SogsTextureData; meta: SogsMetaV2 }> {
    const { signal } = options
    throwIfAborted(signal)

    // 1. 下载 meta.json
    const metaAttempt = beginGSplatResourceAttempt(options.telemetry, {
      resourceType: 'sog-meta',
      resourceIdentity: metaUrl,
      resourceUrl: metaUrl,
      attempt: options.telemetryAttempt,
    })
    let metaBuffer: ArrayBuffer
    try {
      const response = await fetch(metaUrl, SogsParser.fetchInit(options))
      metaAttempt.markResponse(response)
      if (!response.ok) {
        metaAttempt.fail(new Error('SOG metadata request failed'), `http-${response.status}`)
        throw new Error(`Failed to load meta.json: ${response.status}`)
      }
      metaBuffer = await response.arrayBuffer()
      throwIfAborted(signal)
      metaAttempt.complete(metaBuffer.byteLength)
    } catch (error) {
      metaAttempt.fail(error)
      throw error
    }

    let meta = measureGSplatLoadStageSync(
      options.telemetry,
      {
        resourceType: 'sog-meta',
        resourceIdentity: metaUrl,
        resourceUrl: metaUrl,
        attempt: options.telemetryAttempt,
        stage: 'metadata-parse',
      },
      () => JSON.parse(new TextDecoder().decode(metaBuffer)) as SogsMeta
    )

    // 2. 升级 v1 到 v2 格式
    if (isSogsMetaV1(meta)) {
      log.warn(
        '[SogsParser] Loading SOG v1 data which is deprecated. Please recompress your scene with latest tools.'
      )
      meta = SogsParser.upgradeMeta(meta)
    }

    // meta 现在是 SogsMetaV2 类型
    const metaV2 = createEffectiveSogsMeta(meta as SogsMetaV2, options.loadSHN ?? true)

    // 3. 计算基础 URL
    const baseUrl = new URL(metaUrl, window.location.href)

    // 4. 收集所有纹理 URL
    const subs = ['means', 'quats', 'scales', 'sh0', 'shN'] as const
    const textureUrls: { [key: string]: string[] } = {}

    for (const sub of subs) {
      const files = metaV2[sub]?.files ?? []
      textureUrls[sub] = files.map((filename: string) => {
        if (options.mapUrl) {
          return options.mapUrl(filename)
        }
        return new URL(filename, baseUrl).toString()
      })
    }

    // 5. 并行加载所有纹理
    const textureController = new AbortController()
    const abortTextures = () => textureController.abort()
    if (signal) {
      if (signal.aborted) {
        abortTextures()
      } else {
        signal.addEventListener('abort', abortTextures, { once: true })
      }
    }
    const textureOptions: SogsParserLoadOptions = {
      ...options,
      signal: textureController.signal,
    }
    const loadPromises: Promise<THREE.Texture>[] = []
    const textureKeys: string[] = []

    // means 分高低位
    if (textureUrls.means[0]) {
      loadPromises.push(SogsParser.loadTexture(textureUrls.means[0], textureOptions))
      textureKeys.push('means_l')
    }
    if (textureUrls.means[1]) {
      loadPromises.push(SogsParser.loadTexture(textureUrls.means[1], textureOptions))
      textureKeys.push('means_u')
    }

    // quats
    if (textureUrls.quats[0]) {
      loadPromises.push(SogsParser.loadTexture(textureUrls.quats[0], textureOptions))
      textureKeys.push('quats')
    }

    // scales
    if (textureUrls.scales[0]) {
      loadPromises.push(SogsParser.loadTexture(textureUrls.scales[0], textureOptions))
      textureKeys.push('scales')
    }

    // sh0
    if (textureUrls.sh0[0]) {
      loadPromises.push(SogsParser.loadTexture(textureUrls.sh0[0], textureOptions))
      textureKeys.push('sh0')
    }

    // shN (如果有)
    if (textureUrls.shN && textureUrls.shN.length > 0) {
      if (textureUrls.shN[0]) {
        loadPromises.push(SogsParser.loadTexture(textureUrls.shN[0], textureOptions))
        textureKeys.push('sh_centroids')
      }
      if (textureUrls.shN[1]) {
        loadPromises.push(SogsParser.loadTexture(textureUrls.shN[1], textureOptions))
        textureKeys.push('sh_labels')
      }
    }

    // 等待所有纹理加载完成
    let loaded = 0
    const total = loadPromises.length

    let firstError: unknown
    try {
      const settled = await Promise.allSettled(
        loadPromises.map(async (promise) => {
          let texture: THREE.Texture | undefined
          try {
            texture = await promise
            throwIfAborted(textureController.signal)
            loaded++
            options.onProgress?.(loaded, total)
            return texture
          } catch (error) {
            texture?.dispose()
            if (firstError === undefined) {
              firstError = error
              textureController.abort()
            }
            throw error
          }
        })
      )
      const textures = settled.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value] : []
      )
      const rejection = settled.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      )

      if (signal?.aborted || rejection) {
        for (const texture of textures) {
          texture.dispose()
        }
        if (signal?.aborted) {
          throw createAbortError()
        }
        throw firstError ?? rejection?.reason
      }

      // 6. 组装纹理数据
      const textureData: SogsTextureData = {
        means_l: textures[textureKeys.indexOf('means_l')],
        means_u: textures[textureKeys.indexOf('means_u')],
        quats: textures[textureKeys.indexOf('quats')],
        scales: textures[textureKeys.indexOf('scales')],
        sh0: textures[textureKeys.indexOf('sh0')],
      }

      if (textureKeys.includes('sh_centroids')) {
        textureData.sh_centroids = textures[textureKeys.indexOf('sh_centroids')]
      }
      if (textureKeys.includes('sh_labels')) {
        textureData.sh_labels = textures[textureKeys.indexOf('sh_labels')]
      }

      return { textures: textureData, meta: metaV2 }
    } finally {
      signal?.removeEventListener('abort', abortTextures)
    }
  }

  /**
   * 释放纹理资源。
   *
   * @param textures - 待释放的 SOGS 纹理集合
   */
  static disposeTextures(textures: SogsTextureData): void {
    textures.means_l?.dispose()
    textures.means_u?.dispose()
    textures.quats?.dispose()
    textures.scales?.dispose()
    textures.sh0?.dispose()
    textures.sh_centroids?.dispose()
    textures.sh_labels?.dispose()
  }
}
